import React, { useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, Select, MenuItem, Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Avatar,
  Menu,
  Skeleton,
} from '@mui/material';

import { ReactComponent as MoreHorizIcon } from "../../../../assets/images/menukb.svg";
import moment from 'moment';
import SearchInput from '../../../../components/common/SearchInputField';
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { ReactComponent as Visitor } from "../../../../assets/images/visitor.svg";
import { ReactComponent as Person } from "../../../../assets/images/person.svg";
import FeedbackQueueSidebar from '../../../../components/dashboardcomponents/KnowledgeBase/FeedBackQueue/FeedbackQueueSidebar';
import AssignFeedbackQueue from '../../../../components/dashboardcomponents/KnowledgeBase/FeedBackQueue/AssignFeedbackQueue';
import { getKbFeedback, getKbFeedbackById } from '../../../../store/features/getKbFeedbackQueue';
import { completeFeedback } from '../../../../store/features/completeFeedbackQueue';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NoDataFound from "../../../../components/dashboardcomponents/Traffic/NoDataFound";
import { deleteKbFeedback } from '../../../../store/features/deleteFeedbackQueue';
import GroupSelectDropdown from '../../../../components/dashboardcomponents/ProfileComponents/GroupSelectDropdown';
import { useAnalyticsGroupId } from '../../../../hooks/useAnalyticsGroupId';
import useDebounce from '../../../../hooks/useDebounce';
import { isHeaderInlinePageTitleEnabled } from '../../../../config/featureFlags';
import '../../TicketManagment/style.css';


const TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "in progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
];

const FeedbackQueue = () => {
  const dispatch = useDispatch();
  const { loading, error, feedbacks, totalCount, totalPages } = useSelector((state) => state.KbFeedbackQueue);
  const businessId = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const feedbackIdParam = searchParams.get('feedbackId');
  const groupIdParam = searchParams.get('group_id');
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const debouncedSearch = useDebounce(searchQuery, 400);
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [status, setStatus] = useState("");
  const { selectedGroupId, setSelectedGroupId, syncGroupId } = useAnalyticsGroupId();
  const userData = useSelector((state) => state.loginToken);
  const kbRole = userData?.response?.user?.kbRole?.role
  const isLoading = !selectedGroupId || loading;
  // Map Redux feedbacks to rows before paint so revisits do not flash empty NoDataFound.
  useLayoutEffect(() => {
    setRows(
      feedbacks.map((fb) => ({
        id: fb.id,
        title: fb.article,
        status: fb.status.charAt(0).toUpperCase() + fb.status.slice(1),
        assignee: fb?.assigneeId?.full_name || '-',
        source: fb.source.charAt(0).toUpperCase() + fb.source.slice(1),
        dateSubmitted: fb.updated,
        fullData: fb,
      }))
    );
  }, [feedbacks]);

  // Sync selected group from notification deep-link (group_id query)
  useEffect(() => {
    if (!groupIdParam) return;
    if (String(groupIdParam) === String(selectedGroupId)) return;
    syncGroupId(groupIdParam);
    setPage(0);
  }, [groupIdParam, selectedGroupId, syncGroupId]);

  // Fetch data on mount or when filters change
  useEffect(() => {
    if (businessId && selectedGroupId) {
      dispatch(getKbFeedback({
        business_id: businessId,
        group_id: selectedGroupId,
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch
      }));
    }
  }, [dispatch, businessId, selectedGroupId, page, debouncedSearch]);

  const feedbackRowFromMapped = (fromFb) => ({
    id: fromFb.id,
    title: fromFb.article,
    status: fromFb.status.charAt(0).toUpperCase() + fromFb.status.slice(1),
    assignee: fromFb?.assigneeId?.full_name || '-',
    source: fromFb.source.charAt(0).toUpperCase() + fromFb.source.slice(1),
    dateSubmitted: fromFb.updated,
    fullData: fromFb,
  });

  useEffect(() => {
    if (!feedbackIdParam || !businessId || !selectedGroupId) return;
    // Wait until notification group is applied before fetching/opening
    if (groupIdParam && String(groupIdParam) !== String(selectedGroupId)) return;

    const fid = String(feedbackIdParam).trim();
    if (!fid) return;

    const clearFeedbackParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('feedbackId');
          next.delete('group_id');
          return next;
        },
        { replace: true }
      );
    };

    const fromFb = feedbacks.find(
      (f) => String(f.id) === fid || String(f._id) === fid
    );

    if (fromFb) {
      setSelectedRow(feedbackRowFromMapped(fromFb));
      setIsOpen(true);
      setIsAssignOpen(false);
      clearFeedbackParam();
      return;
    }

    if (loading) return;

    let cancelled = false;

    (async () => {
      try {
        const fb = await dispatch(
          getKbFeedbackById({ business_id: businessId, group_id: selectedGroupId, feedbackId: fid })
        ).unwrap();
        if (cancelled) return;
        setSelectedRow(feedbackRowFromMapped(fb));
        setIsOpen(true);
        setIsAssignOpen(false);
        clearFeedbackParam();
      } catch (e) {
        if (!cancelled) {
          const msg =
            typeof e === 'string'
              ? e
              : e?.message || 'Could not open this feedback';
          toast.error(msg);
          clearFeedbackParam();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    feedbackIdParam,
    groupIdParam,
    businessId,
    selectedGroupId,
    feedbacks,
    loading,
    dispatch,
    setSearchParams,
  ]);

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };
  const handleComplete = async () => {
    setIsOpen(false);
    if (!selectedRow) return;

    try {
      const businessIdComplete = businessId; // Use from row or global

      const completeResponse = await dispatch(completeFeedback({
        feedbackId: selectedRow.id,
        business_id: businessIdComplete,
        group_id: selectedGroupId,
      }));

      // Check for success (adjust based on API response structure)
      if (completeResponse.payload?.status !== 200) {
        throw new Error(completeResponse.payload?.message || 'Complete failed');
      }

      toast.success("Feedback Completed Successfully.");

      // Build updated item (update status to 'Completed')
      const updatedItem = {
        ...selectedRow,
        status: 'Completed',
        fullData: {
          ...selectedRow.fullData,
          status: 'completed', // Assuming backend uses lowercase
          completedAt: new Date().toISOString(), // Optional: add timestamp
        }
      };

      // Update local rows
      setRows(rows.map(r => r.id === updatedItem.id ? updatedItem : r));

      // Optionally refetch data or notify parent
      // dispatch(getKbFeedback({ ... })); // If needed to refresh from server

      handleMenuClose();
    } catch (error) {
      console.error('Error completing feedback:', error);
      toast.error(error.message || 'Failed to complete feedback');
    }
  };

  const handleEditCategory = () => {
    const articleId = selectedRow?.fullData?.articleId?._id;

    if (!articleId) {
      toast.error("Article not found");
      handleMenuClose();
      return;
    }

    navigate(`/kb/article/${articleId}`);
    handleMenuClose();
  };


  const handleDeleteCategory = () => {

    handleMenuClose();
  };

  const handleOpenAssign = () => {
    // setFeedbackData(selectedRow);
    setIsAssignOpen(true);
    handleMenuClose();
  };
  const handleAssignSuccess = (updatedRow) => {
    // Update the row with the correct assignee name from fullData
    const updatedRowWithAssignee = {
      ...updatedRow,
      assignee: updatedRow.fullData?.assigneeId?.full_name || '-', // Extract name from fullData
      status: updatedRow.status || 'In Progress' // Ensure status is properly set
    };

    setRows(rows.map(r => r.id === updatedRowWithAssignee.id ? updatedRowWithAssignee : r));
    setIsAssignOpen(false);

  }


  const handleOpenAssignFromSidebar = () => {
    setIsAssignOpen(true);
    setAnchorEl(null);
    setIsOpen(false); // Close sidebar
  };
  const counts = useMemo(() => {
    const c = { all: rows.length, new: 0, "in progress": 0, completed: 0 };
    rows.forEach(r => {
      let k = (r.status || "").toLowerCase().trim();
      if (k.includes('progress')) k = "in progress";
      if (c[k] !== undefined) c[k]++;
    });
    return c;
  }, [rows]);

  // Search is applied server-side, so only tab/source narrowing happens here.
  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      // Filter by tab
      if (tab !== "all") {
        let statusKey = (r.status || "").toLowerCase().trim();
        if (statusKey.includes('progress')) statusKey = "in progress";
        if (statusKey !== tab) return false;
      }

      // Filter by source select
      if (status && r.source !== status) return false;

      return true;
    });
  }, [rows, tab, status]);

  // `rows` is already one server page, so no client-side slicing.
  const visibleRows = filteredRows;

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };
  const handleDeleteFeedBack = async () => {
    setIsOpen(false);
    if (!selectedRow) return;

    try {
      const deleteResponse = await dispatch(deleteKbFeedback({ feedbackId: selectedRow.id, group_id: selectedGroupId }));

      // Check for success (adjust based on API response structure)




      // Remove the deleted item from local rows
      setRows(rows.filter(r => r.id !== selectedRow.id));

      // Optionally adjust totalCount for pagination, but since we're refetching on changes, it might not be necessary
      // For immediate UI update, you could decrement totalCount if tracking it locally

      handleMenuClose();
    } catch (error) {

    }
  };

  const handleSourceChange = (e) => {
    setStatus(e.target.value);
    setPage(0);
  };

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  const renderSkeletonRows = () => (
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell sx={{ borderBottom: '1px solid #EEF2F6' }}>
          <Skeleton variant="text" width="80%" height={20} />
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #EEF2F6' }}>
          <Skeleton variant="rounded" width={70} height={24} />
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #EEF2F6' }}>
          <Skeleton variant="text" width="60%" height={20} />
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #EEF2F6' }}>
          <Skeleton variant="rounded" width={80} height={24} />
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #EEF2F6' }}>
          <Skeleton variant="text" width="40%" height={20} />
        </TableCell>
        <TableCell>
          <Skeleton variant="circular" width={24} height={24} />
        </TableCell>
      </TableRow>
    ))
  );

  return (
    <Box
      sx={{
        padding: '24px',
        paddingTop: isHeaderInlinePageTitleEnabled ? 0 : '16px',
        paddingBottom: '8px',
        backgroundColor: '#fff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div>
        {/* Legacy in-page title + group — hidden when header shows them (Application V2) */}
        {!isHeaderInlinePageTitleEnabled && (
          <div className="profile-header-row">
            <p className="Ticket-text">Feedback Queue</p>
            <GroupSelectDropdown
              onSelect={setSelectedGroupId}
              value={selectedGroupId}
            />
          </div>
        )}
        <div
          className="Ticket-main-header"
          style={{
            marginTop: isHeaderInlinePageTitleEnabled ? 0 : undefined,
          }}
        >
          <div className="Ticket-text-section">
            {TABS.map((t) => (
              <p
                key={t.key}
                className={`ticket-header-text ${tab === t.key ? "TicketHeaderActive" : ""}`}
                onClick={() => { setTab(t.key); setPage(0); }}
              >
                {t.label} ({counts[t.key] || 0})
              </p>
            ))}
          </div>
        </div>
      </div>
      <div>
      </div>
      <Box style={{ display: 'flex', gap: '8px', marginTop: '19px' }}>

        <SearchInput
          title=""
          placeholder="Search by article title"
          className="SearchInputField-feedback"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <Select
          value={status}
          onChange={handleSourceChange}
          IconComponent={Dropdown}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return <span style={{
                color: "#405261",
                fontSize: "14px",
                fontFamily: 'Inter',
                fontWeight: 400,
              }}>Source</span>;
            }
            return selected.charAt(0).toUpperCase() + selected.slice(1);
          }}
          sx={{
            height: "40px",
            width: "160px",
            marginTop: '5px',
            fontSize: "14px",
            color: "#405261",
            "& .MuiSelect-select": {
              padding: "0px 12px",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1px solid #D5DCE1",
                marginTop: "4px",
                width: "120px",
                padding: '0px 8px',
                borderRadius: "8px",
                "& .MuiMenuItem-root": {
                  fontSize: "14px",
                  padding: "8px 8px",
                  color: "#405261",
                  "&:hover": {
                    color: '#061522',
                    borderRadius: "8px",
                    background: "#F9FAFA",
                  },
                  "&.Mui-selected": {
                    color: '#061522',
                    borderRadius: "8px",
                    background: "#F9FAFA",  // This should override the blue
                    "&:hover": {
                      background: "#F9FAFA",
                      borderRadius: "8px",
                    },
                  },
                  // ADD THESE to completely remove Material-UI's default blue
                  "&.Mui-selected": {
                    borderRadius: "8px",
                    backgroundColor: "#F9FAFA !important",  // Use !important to force override
                  },
                  "&.Mui-focusVisible": {
                    borderRadius: "8px",
                    backgroundColor: "#F9FAFA",
                  },
                },
              },
            },
          }}

        >
          <MenuItem key="" value="">
            All
          </MenuItem>
          {["Visitor", "Internal"].map((option) => (
            <MenuItem key={option} value={option}>
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* <ReviewQueueToolbar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      /> */}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginTop: '24px' }}>
        <TableContainer
          sx={{
            flex: 1,
            marginTop: '0px',
            width: '100%',
            width: '100%',
            boxShadow: "none",
            marginBottom: visibleRows?.length !== 0 ? '40px' : '0px',
            overflowY: "auto",
          }}
        >
          <Table stickyHeader sx={{
            "& .MuiTableCell-root": {
              paddingLeft: "0px !important",

              height: '64px',



            },

          }}>
            <TableHead sx={{
              "& .MuiTableCell-root": {
                paddingTop: "0px !important",
                paddingBottom: "10px !important",
                zIndex: 1,
                borderBottom: '1px solid #EEF2F6',
                height: "auto",
              },
            }}>
              <TableRow >
                <TableCell
                  sx={{
                    minWidth: '400px',
                    background: '#fff',
                    height: 48,
                    borderBottom: '1px solid #EEF2F6',
                    color: '#405261',
                    fontSize: 12,
                    fontWeight: 400,
                    py: 0,
                  }}
                >
                  Articles
                </TableCell>
                <TableCell
                  sx={{
                    background: '#fff',
                    height: 48,
                    borderBottom: '1px solid #EEF2F6',
                    color: '#405261',
                    fontSize: 12,
                    fontWeight: 400,
                    py: 0,
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    background: '#fff',
                    height: 48,
                    borderBottom: '1px solid #EEF2F6',
                    color: '#405261',
                    fontSize: 12,
                    fontWeight: 400,
                    py: 0,
                  }}
                >
                  Assignee
                </TableCell>
                <TableCell
                  sx={{
                    background: '#fff',
                    height: 48,
                    borderBottom: '1px solid #EEF2F6',
                    color: '#405261',
                    fontSize: 12,
                    fontWeight: 400,
                    py: 0,
                  }}
                >
                  Source
                </TableCell>
                <TableCell
                  sx={{
                    background: '#fff',
                    height: 48,
                    borderBottom: '1px solid #EEF2F6',
                    color: '#405261',
                    fontSize: 12,
                    fontWeight: 400,
                    py: 0,
                  }}
                >
                  Date Submitted
                </TableCell>
                <TableCell

                />
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                renderSkeletonRows()
              ) : visibleRows.length === 0 ? (
                <TableRow sx={{ '& td': { border: 'none !important' } }}>
                  <TableCell
                    colSpan={7}
                    sx={{
                      border: 'none !important',
                      borderBottom: 'none !important'
                    }}
                  >
                    <NoDataFound
                      containerStyle={{ height: '53vh' }}
                      isForKb={true}
                      title="No feedback queue found"
                      description="This segment is empty. Try another one or come back later"
                    />
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows?.map((row) => (
                  <TableRow

                    key={row.id}
                    onClick={() => {
                      setSelectedRow(row);
                      setIsOpen(true);
                      setIsAssignOpen(false)
                    }}

                    sx={{
                      cursor: 'pointer',
                      height: 64,
                      '&:hover': {
                        backgroundColor: '#F7F8F9'
                      },
                      borderTop: '1px solid #EEF2F6',
                      '& td': { borderBottom: '1px solid #EEF2F6' }
                    }}
                  >
                    {/* Title Column - with drag handle + folder icon */}
                    <TableCell sx={{ borderBottom: '1px solid #EEF2F6', }}>

                      <Typography
                        sx={{
                          color: '#061522',
                          fontSize: 14,
                          fontWeight: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row?.title}
                      </Typography>

                    </TableCell>

                    {/* Status - chip style like Visibility */}
                    <TableCell sx={{ borderBottom: '1px solid #EEF2F6' }}>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 24,
                          width: row?.status === 'New' ? '48px' : row?.status === 'In Progress' ? '95px' : '88px',
                          borderRadius: '5px',
                          border: row?.status === 'Completed' ? '1px solid #148DFD' : row?.status === 'New' ? '1px solid #52C41A' : '1px solid #FA8C16',
                          background: row?.status === 'Completed' ? '#F3FBFF' : row?.status === 'New' ? '#F6FFED' : '#FFF7E6',
                          color: '#405261',
                        }}
                      >
                        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                          {row?.status}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Assignee + Three-dots menu */}
                    < TableCell sx={{ borderBottom: '1px solid #EEF2F6' }}>

                      <Typography
                        sx={{
                          color: '#405261',
                          fontSize: 14,
                          fontWeight: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row?.assignee || '-'}
                      </Typography>

                    </TableCell>

                    {/* Source with icon */}
                    <TableCell sx={{ borderBottom: '1px solid #EEF2F6' }}>
                      <Box sx={{
                        display: 'flex', width: '80px',
                        justifyContent: 'center',

                        height: '24px', alignItems: 'center', gap: 0.5, borderRadius: '5px', border: '1px solid #D5DCE1',
                        background: row.source === 'Visitor' ? '#FFF' : '#F9FAFA'
                      }}>
                        {row?.source === 'Visitor' ? <Visitor /> : <Person />}
                        <Typography sx={{ color: '#405261', fontSize: 12, fontWeight: 400 }}>
                          {row?.source}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Date Submitted */}
                    < TableCell sx={{ borderBottom: '1px solid #EEF2F6' }}>
                      <Typography sx={{ color: '#405261', fontSize: 14, fontWeight: 400 }}>
                        {row?.dateSubmitted}
                      </Typography>
                    </TableCell>
                    < TableCell padding='none' sx={{ textAlign: 'right' }} >
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, row)}
                        size="small"
                        sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                      >
                        <MoreHorizIcon sx={{ color: '#405261' }} />
                      </IconButton>

                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Footer */}
        {
          !isLoading && totalCount > rowsPerPage && (
            <Box
              sx={{
                borderTop: '1px solid #E5E9EC',
                background: '#fff',
                padding: '16px 0px',
                flexShrink: 0,
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({ from, to, count }) => (
                  <Typography sx={{ color: '#405261', fontSize: 14, fontWeight: 400 }}>
                    Feedback Queue {from}-{Math.min(to, count)}
                  </Typography>
                )}
                ActionsComponent={(props) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <Button
                      onClick={(e) => props.onPageChange(e, props.page - 1)}
                      disabled={props.page === 0}
                      sx={{
                        width: { xs: '100px', sm: '128px' },
                        height: 40,
                        borderRadius: '8px',
                        border: '1px solid #D5DCE1',
                        background: '#FFF',
                        boxShadow: '0px 1px 2px 0px rgba(6, 21, 34, 0.05)',
                        color: '#405261',
                        fontSize: 14,
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#FFFFFF', borderColor: '#C5CED6' },
                        '&.Mui-disabled': { color: '#93A1B0', borderColor: '#C5CED6', bgcolor: '#F9FAFA' },
                      }}
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={(e) => props.onPageChange(e, props.page + 1)}
                      disabled={props.page >= totalPages - 1}
                      sx={{
                        width: { xs: '80px', sm: '88px' },
                        height: 40,
                        borderRadius: '8px',
                        border: '1px solid #D5DCE1',
                        background: '#FFFFFF',
                        boxShadow: '0px 1px 2px 0px rgba(6, 21, 34, 0.05)',
                        color: '#405261',
                        fontSize: 14,
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#FFFFFF', borderColor: '#C5CED6' },
                        '&.Mui-disabled': { color: '#93A1B0', borderColor: '#C5CED6', bgcolor: '#F9FAFA' },
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                )}
                sx={{
                  ".MuiTablePagination-toolbar": {
                    padding: 0,
                    minHeight: "unset",
                    display: "flex",
                    justifyContent: "space-between",
                  },
                  ".MuiTablePagination-spacer": {
                    display: "none",
                  },
                  ".MuiTablePagination-selectLabel": {
                    display: "none",
                  },
                  ".MuiTablePagination-select": {
                    display: "none",
                  },
                  ".MuiTablePagination-selectIcon": {
                    display: "none",
                  },
                }}

              />
            </Box>
          )
        }
      </Box >
      <FeedbackQueueSidebar isOpen={isOpen} onClose={() => setIsOpen(false)} selectedItem={selectedRow} onAssignClick={handleOpenAssignFromSidebar} handleComplete={handleComplete} handleDelete={handleDeleteFeedBack} />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 0,
          sx: {
            filter: "none",
            color: '#405261',
            mt: '-5px',
            width: 184,
            paddingX: "8px",
            borderRadius: "8px",
            border: "1px solid #D5DCE1",
            boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
            "& .MuiMenuItem-root": {
              px: "8px",
              py: 1,
              fontSize: '14px',
              fontFamily: 'Inter',
              color: '#405261',
              "&:hover": { color: "#061522", borderRadius: "8px", background: "#F9FAFA !important" },
            },
          },
        }}
      >


        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleEditCategory();
          }}
        >
          Edit Article
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleOpenAssignFromSidebar();
          }}
        >
          Assign
        </MenuItem>
        {selectedRow?.status !== 'Completed' && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              handleComplete();
            }}
          >
            Complete
          </MenuItem>
        )}
        <MenuItem
          disabled={kbRole !== 'editor'}
          sx={{
            ...(kbRole === 'editor' && {
              color: '#FF4D4F !important',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              "&:hover": {
                color: '#FF4D4F !important',
                background: "#F9FAFA !important",
                borderRadius: '8px'
              },
            }),
            ...(kbRole !== 'editor' && {
              color: 'rgba(64, 82, 97, 0.4)',
              "&:hover": {
                color: 'rgba(64, 82, 97, 0.4)',
                background: 'transparent !important',
                borderRadius: '8px'
              },
            }),
          }}
          onClick={(event) => {
            event.stopPropagation();
            if (kbRole === 'editor') {
              handleDeleteFeedBack()
            }
          }}
        >
          Delete
        </MenuItem>
      </Menu>
      <AssignFeedbackQueue
        isOpen={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
        selectedItem={selectedRow}
        onAssignSuccess={handleAssignSuccess}
        groupId={selectedGroupId}
      />
    </Box >
  );
};

export default FeedbackQueue;